import { locale } from '../services/LocaleService.js';

export class Modal {
    constructor() {
        this.container = null;
        this.init();
    }

    init() {
        // Create container if not exists
        if (document.getElementById('global-modal-container')) return;

        const div = document.createElement('div');
        div.id = 'global-modal-container';
        // [HIERARCHY] Z-Index 20000 (Must be higher than FAB z-10000)
        div.className = 'fixed inset-0 z-[20000] pointer-events-none flex items-center justify-center';
        div.style.display = 'none'; // [FIX] Hidden by default
        div.innerHTML = `
            <div id="modal-backdrop" class="absolute inset-0 bg-black/60 opacity-0 transition-opacity duration-300 backdrop-blur-sm"></div>
            <div id="modal-box" class="bg-white dark:bg-zinc-800 rounded-2xl shadow-2xl w-[90%] max-w-sm p-6 transform scale-95 opacity-0 transition-all duration-300 relative pointer-events-none border border-gray-100 dark:border-zinc-700">
                <h3 id="modal-title" class="text-lg font-bold text-gray-900 dark:text-white mb-2"></h3>
                <p id="modal-message" class="text-gray-600 dark:text-gray-300 text-sm mb-4 leading-relaxed whitespace-pre-wrap break-words"></p>
                
                <!-- Input Container for Prompt -->
                <div id="modal-input-container" class="hidden mb-6">
                    <input type="text" id="modal-input" class="w-full px-4 py-2 bg-gray-50 dark:bg-zinc-900 border border-gray-200 dark:border-zinc-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all" autocomplete="off">
                </div>

                <div id="modal-actions" class="flex gap-3 justify-end">
                    <!-- Buttons injected here -->
                </div>
            </div>
        `;
        document.body.appendChild(div);

        this.container = div;
        this.backdrop = div.querySelector('#modal-backdrop');
        this.box = div.querySelector('#modal-box');
        this.titleEl = div.querySelector('#modal-title');
        this.messageEl = div.querySelector('#modal-message');
        this.inputContainer = div.querySelector('#modal-input-container');
        this.inputEl = div.querySelector('#modal-input');
        this.actionsEl = div.querySelector('#modal-actions');
    }

    show(message, title = 'Alim', type = 'alert', defaultValue = '', theme = 'default') {
        // [Safety] Re-init if container was removed (e.g. by ModalManager cleanup)
        if (!document.body.contains(this.container)) {
            this.init();
        }

        return new Promise((resolve) => {
            // [FIX] Ensure visible
            this.container.style.display = 'flex';

            // Setup content
            this.titleEl.textContent = title;
            // [FIX] Support HTML tags (e.g. <br>)
            this.messageEl.innerHTML = message;
            this.actionsEl.innerHTML = '';

            // Reset Input
            this.inputContainer.classList.add('hidden');
            this.inputEl.value = '';

            // [THEME] Glassmorphism Support
            if (theme === 'glass') {
                this.box.className = 'bg-black/80 backdrop-blur-md rounded-2xl shadow-2xl w-[90%] max-w-sm p-6 transform scale-95 opacity-0 transition-all duration-300 relative pointer-events-none border border-white/10 text-white';
                this.titleEl.className = 'text-lg font-bold text-white mb-2';
                this.messageEl.className = 'text-gray-300 text-sm mb-4 leading-relaxed whitespace-pre-wrap';
            } else {
                // Default Theme (Reset)
                this.box.className = 'bg-white dark:bg-zinc-800 rounded-2xl shadow-2xl w-[90%] max-w-sm p-6 transform scale-95 opacity-0 transition-all duration-300 relative pointer-events-none border border-gray-100 dark:border-zinc-700';
                this.titleEl.className = 'text-lg font-bold text-gray-900 dark:text-white mb-2';
                this.messageEl.className = 'text-gray-600 dark:text-gray-300 text-sm mb-4 leading-relaxed whitespace-pre-wrap';
            }

            // Handle Prompt Input
            if (type === 'prompt') {
                this.inputContainer.classList.remove('hidden');
                this.inputEl.value = defaultValue;
                // Focus input after transition
                setTimeout(() => this.inputEl.focus(), 100);

                // Allow Enter key to submit
                this.inputEl.onkeydown = (e) => {
                    if (e.key === 'Enter') {
                        this.close();
                        resolve(this.inputEl.value);
                    }
                };
            }

            // Setup buttons
            if (type === 'confirm' || type === 'prompt') {
                const cancelBtn = document.createElement('button');
                // Theme-aware Cancel Button
                const cancelClass = theme === 'glass'
                    ? 'px-4 py-2 rounded-xl bg-white/10 text-gray-300 font-bold text-sm hover:bg-white/20 transition-colors pointer-events-auto'
                    : 'px-4 py-2 rounded-xl bg-gray-100 dark:bg-zinc-700 text-gray-700 dark:text-gray-200 font-bold text-sm hover:bg-gray-200 dark:hover:bg-zinc-600 transition-colors pointer-events-auto';

                cancelBtn.className = cancelClass;
                cancelBtn.textContent = locale.t('common.cancel');
                cancelBtn.onclick = () => {
                    this.close();
                    resolve(type === 'prompt' ? null : false);
                };

                const confirmBtn = document.createElement('button');
                confirmBtn.className = 'px-4 py-2 rounded-xl bg-indigo-600 text-white font-bold text-sm hover:bg-indigo-500 shadow-lg shadow-indigo-500/30 transition-all pointer-events-auto';
                confirmBtn.textContent = locale.t('common.confirm');
                confirmBtn.onclick = () => {
                    this.close();
                    resolve(type === 'prompt' ? this.inputEl.value : true);
                };

                this.actionsEl.appendChild(cancelBtn);
                this.actionsEl.appendChild(confirmBtn);
            } else {
                // Alert
                const okBtn = document.createElement('button');
                okBtn.className = 'w-full px-4 py-2 rounded-xl bg-indigo-600 text-white font-bold text-sm hover:bg-indigo-500 shadow-lg shadow-indigo-500/30 transition-all pointer-events-auto';
                okBtn.textContent = locale.t('common.confirm');
                okBtn.onclick = () => { this.close(); resolve(true); };
                this.actionsEl.appendChild(okBtn);
            }

            // Show
            this.container.classList.remove('pointer-events-none');
            this.box.classList.remove('pointer-events-none'); // Enable interactions
            this.box.classList.add('pointer-events-auto');

            // Small delay to allow transition
            requestAnimationFrame(() => {
                this.backdrop.classList.remove('opacity-0');
                this.box.classList.remove('scale-95', 'opacity-0');
                this.box.classList.add('scale-100', 'opacity-100');
            });
        });
    }

    alert(message, title = '알림', theme = 'default') {
        return this.show(message, title, 'alert', '', theme);
    }

    confirm(message, title = '확인', theme = 'default') {
        const p = this.show(message, title, 'confirm', '', theme);
        const btns = this.actionsEl.querySelectorAll('button');
        if (btns.length === 2) {
            btns[0].textContent = '취소';
            btns[1].textContent = '확인';
        }
        return p;
    }

    prompt(message, defaultValue = '', title = '입력') {
        const p = this.show(message, title, 'prompt', defaultValue);
        const btns = this.actionsEl.querySelectorAll('button');
        if (btns.length === 2) {
            btns[0].textContent = '취소';
            btns[1].textContent = '제출';
        }
        return p;
    }

    close() {
        this.backdrop.classList.add('opacity-0');
        this.box.classList.remove('scale-100', 'opacity-100', 'pointer-events-auto');
        this.box.classList.add('scale-95', 'opacity-0', 'pointer-events-none'); // Disable interactions immediately

        // Remove custom styles if any
        this.box.style.background = '';
        this.box.style.border = '';

        setTimeout(() => {
            if (this.container) {
                this.container.classList.add('pointer-events-none');
                this.container.style.display = 'none'; // [FIX] Hide completely
            }
        }, 300);
    }

    showVerificationSuccess(email) {
        return new Promise((resolve) => {
            // [FIX] Ensure visible (Restore from display: none)
            if (this.container) this.container.style.display = 'flex';

            // [UX] Premium Space Theme Override
            this.box.className = 'overflow-hidden relative w-full max-w-md rounded-3xl shadow-2xl transform scale-95 opacity-0 transition-all duration-300 pointer-events-auto border border-white/10';

            // Background Image
            this.box.style.background = "url('/login.webp') center/cover no-repeat";

            // Content (Inner Glass)
            this.titleEl.innerHTML = '';
            this.messageEl.innerHTML = '';
            this.actionsEl.innerHTML = '';

            const contentHTML = `
                <div class="relative z-10 bg-black/80 backdrop-blur-md p-8 text-center h-full flex flex-col items-center justify-center">
                    <!-- Icon -->
                    <div class="w-20 h-20 rounded-full bg-indigo-500/20 flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(99,102,241,0.3)] animate-pulse-slow">
                        <span class="material-symbols-outlined text-4xl text-indigo-300">rocket_launch</span>
                    </div>
                    
                    <!-- Title -->
                    <h3 class="text-2xl font-bold text-white mb-2 tracking-tight">차원 진입 승인 키 발송</h3>
                    
                    <!-- Subtitle -->
                    <p class="text-indigo-200 text-sm mb-6 font-medium">SSULWHA WORLD</p>

                    <!-- Body -->
                    <div class="text-gray-300 text-sm leading-relaxed mb-8">
                        <span class="text-white font-bold border-b border-indigo-500/50 pb-0.5">${email}</span> 으로<br>
                        탑승권(인증 링크)을 전송했습니다.<br>
                        <br>
                        이메일을 확인하여 <strong>차원 연결</strong>을 완료해주세요.
                    </div>

                    <!-- Button -->
                    <button id="modal-confirm-btn" class="w-full py-4 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold text-lg shadow-lg shadow-indigo-500/40 transition-all active:scale-[0.98]">
                        확인했습니다
                    </button>
                    
                    <p class="mt-6 text-xs text-gray-500">
                        메일이 오지 않았다면 스팸함을 확인해주세요.
                    </p>
                </div>
            `;

            this.box.innerHTML = contentHTML;

            // Bind Click
            const confirmBtn = this.box.querySelector('#modal-confirm-btn');
            if (confirmBtn) {
                confirmBtn.onclick = () => {
                    this.close();
                    resolve(true);
                };
            }

            // Show
            this.container.classList.remove('pointer-events-none');
            this.box.classList.remove('pointer-events-none');
            this.box.classList.add('pointer-events-auto');

            requestAnimationFrame(() => {
                this.backdrop.classList.remove('opacity-0');
                this.box.classList.remove('scale-95', 'opacity-0');
                this.box.classList.add('scale-100', 'opacity-100');
            });
        });
    }

    showApprovalRequestSuccess() {
        return new Promise((resolve) => {
            // [FIX] Ensure visible
            if (this.container) this.container.style.display = 'flex';

            // [UX] Premium Space Theme Override
            this.box.className = 'overflow-hidden relative w-full max-w-md rounded-3xl shadow-2xl transform scale-95 opacity-0 transition-all duration-300 pointer-events-auto border border-white/10';

            // Background Image (Cosmic/Space)
            this.box.style.background = "url('/login.webp') center/cover no-repeat";

            // Content
            this.titleEl.innerHTML = '';
            this.messageEl.innerHTML = '';
            this.actionsEl.innerHTML = '';

            const title = locale.t('editor.approval_requested');
            const desc = locale.t('editor.approval_desc').replace(/\\n/g, '<br>'); // Handle newlines
            const confirmText = locale.t('common.confirm');

            const contentHTML = `
                <div class="relative z-10 bg-black/80 backdrop-blur-md p-8 text-center h-full flex flex-col items-center justify-center">
                    <!-- Icon -->
                    <div class="w-20 h-20 rounded-full bg-indigo-500/20 flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(99,102,241,0.3)] animate-pulse-slow">
                        <span class="material-symbols-outlined text-4xl text-indigo-300">rocket_launch</span>
                    </div>

                    <!-- Title -->
                    <h3 class="text-2xl font-bold text-white mb-2 tracking-tight">${title}</h3>

                    <!-- Subtitle -->
                    <p class="text-indigo-200 text-sm mb-6 font-medium">MIRINAE CREATORS</p>

                    <!-- Body -->
                    <div class="text-gray-300 text-sm leading-relaxed mb-8">
                        ${desc}
                    </div>

                    <!-- Button -->
                    <button id="modal-confirm-btn" class="w-full py-4 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold text-lg shadow-lg shadow-indigo-500/40 transition-all active:scale-[0.98]">
                        ${confirmText}
                    </button>
                </div>
            `;

            this.box.innerHTML = contentHTML;

            // Bind Click
            const confirmBtn = this.box.querySelector('#modal-confirm-btn');
            if (confirmBtn) {
                confirmBtn.onclick = () => {
                    this.close();
                    resolve(true); // Resolve promise
                };
            }

            // Show
            this.container.classList.remove('pointer-events-none');
            this.box.classList.remove('pointer-events-none');
            this.box.classList.add('pointer-events-auto');

            requestAnimationFrame(() => {
                this.backdrop.classList.remove('opacity-0');
                this.box.classList.remove('scale-95', 'opacity-0');
                this.box.classList.add('scale-100', 'opacity-100');
            });
        });
    }

    async showLanguageSelector(currentLang = 'en') {
        // [Safety] Re-init if container was removed
        if (!document.body.contains(this.container)) {
            this.init();
        }

        return new Promise((resolve) => {
            // [FIX] Ensure visible (Restore from display: none)
            if (this.container) this.container.style.display = 'flex';

            // [UX] Reset styles first
            this.box.className = 'bg-white dark:bg-zinc-800 rounded-2xl shadow-2xl w-[90%] max-w-sm p-6 transform scale-95 opacity-0 transition-all duration-300 relative pointer-events-none border border-gray-100 dark:border-zinc-700';

            // [Layout] Shift Modal Upwards to avoid FAB collision on mobile
            // Add 'mb-32' or similar margin-bottom to push it up
            this.box.classList.add('mb-40');

            // Setup content
            this.titleEl.innerHTML = `<div class="flex items-center gap-2"><span class="text-2xl">🌍</span> <span>${locale.t('modal.translate_title')}</span></div>`;
            this.messageEl.textContent = locale.t('modal.translate_placeholder');
            this.actionsEl.innerHTML = '';

            // Reset Input (Hide default text input)
            this.inputContainer.classList.add('hidden');
            this.inputEl.value = '';

            // --- Custom Select Container ---
            let selectContainer = this.box.querySelector('#modal-select-container');
            if (!selectContainer) {
                selectContainer = document.createElement('div');
                selectContainer.id = 'modal-select-container';
                selectContainer.className = 'mb-6';
                this.messageEl.after(selectContainer);
            }
            selectContainer.classList.remove('hidden');

            const languages = [
                { code: 'en', label: 'English (영어)' },
                { code: 'ja', label: 'Japanese (일본어)' },
                { code: 'zh', label: 'Chinese (중국어 간체)' },
                { code: 'vi', label: 'Vietnamese (베트남어)' },
                { code: 'th', label: 'Thai (태국어)' },
                { code: 'es', label: 'Spanish (스페인어)' },
                { code: 'fr', label: 'French (프랑스어)' },
                { code: 'de', label: 'German (독일어)' },
                { code: 'ru', label: 'Russian (러시아어)' },
                { code: 'ko', label: 'Korean (한국어 - 복귀)' }
            ];

            selectContainer.innerHTML = `
                <div class="relative group">
                    <select id="modal-lang-select" class="w-full px-4 py-4 bg-gray-50 dark:bg-zinc-900 border border-gray-200 dark:border-zinc-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all appearance-none cursor-pointer font-bold text-lg shadow-inner">
                        ${languages.map(l => `<option value="${l.code}" ${l.code === currentLang ? 'selected' : ''}>${l.label}</option>`).join('')}
                    </select>
                    <div class="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-500 dark:text-gray-400 group-hover:text-indigo-500 transition-colors">
                        <span class="material-symbols-outlined text-2xl">expand_more</span>
                    </div>
                </div>
            `;

            const selectEl = selectContainer.querySelector('#modal-lang-select');

            // --- Actions (Vertical Stack for Mobile Accessibility) ---
            this.actionsEl.className = 'flex flex-col gap-3'; // Stack vertical

            // 1. Confirm Button (Primary - Top of Stack)
            const confirmBtn = document.createElement('button');
            confirmBtn.className = 'w-full py-3.5 rounded-xl bg-indigo-600 text-white font-bold text-lg hover:bg-indigo-500 shadow-lg shadow-indigo-500/30 transition-all pointer-events-auto flex items-center justify-center gap-2 active:scale-[0.98]';
            confirmBtn.innerHTML = `<span class="material-symbols-outlined">translate</span> ${locale.t('modal.translate_start')}`;
            confirmBtn.onclick = () => {
                const selected = selectEl.value;
                this.box.classList.remove('mb-40'); // Reset margin
                this.closeInternal(selectContainer);
                resolve(selected);
            };

            // 2. Cancel Button (Secondary - Bottom of Stack)
            const cancelBtn = document.createElement('button');
            cancelBtn.className = 'w-full py-3 rounded-xl bg-gray-100 dark:bg-zinc-700 text-gray-500 dark:text-gray-400 font-bold text-sm hover:bg-gray-200 dark:hover:bg-zinc-600 transition-colors pointer-events-auto';
            cancelBtn.textContent = locale.t('common.cancel');
            cancelBtn.onclick = () => {
                this.box.classList.remove('mb-40'); // Reset margin
                this.closeInternal(selectContainer);
                resolve(null);
            };

            this.actionsEl.appendChild(confirmBtn);
            this.actionsEl.appendChild(cancelBtn);

            // Show Modal
            this.container.classList.remove('pointer-events-none');
            this.box.classList.remove('pointer-events-none');
            this.box.classList.add('pointer-events-auto');

            requestAnimationFrame(() => {
                this.backdrop.classList.remove('opacity-0');
                this.box.classList.remove('scale-95', 'opacity-0');
                this.box.classList.add('scale-100', 'opacity-100');
            });
        });
    }

    // Helper to cleanup custom elements on close
    closeInternal(customElement) {
        this.close();
        // Reset Layout hacks
        this.actionsEl.className = 'flex gap-3 justify-end'; // Revert to default horizontal
        if (customElement) customElement.classList.add('hidden');
    }

    // [VIRAL] Show Publish Success with Share Prompt
    showPublishSuccess(shareUrl, seriesTitle) {
        return new Promise((resolve) => {
            if (this.container) this.container.style.display = 'flex';

            this.box.className = 'overflow-hidden relative w-full max-w-md rounded-3xl shadow-2xl transform scale-95 opacity-0 transition-all duration-300 pointer-events-auto border border-white/10';
            this.box.style.background = "url('/login.webp') center/cover no-repeat";

            this.titleEl.innerHTML = '';
            this.messageEl.innerHTML = '';
            this.actionsEl.innerHTML = '';

            const contentHTML = `
                <div class="relative z-10 bg-black/80 backdrop-blur-md p-8 text-center h-full flex flex-col items-center justify-center">
                    <div class="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(34,197,94,0.3)] animate-pulse-slow">
                        <span class="material-symbols-outlined text-4xl text-green-300">rocket_launch</span>
                    </div>
                    <h3 class="text-2xl font-bold text-white mb-2 tracking-tight">🎉 발행 완료!</h3>
                    <p class="text-green-200 text-sm mb-6 font-medium">독자에게 공개되었습니다</p>
                    <div class="w-full bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
                        <p class="text-xs text-zinc-400 mb-2 font-bold flex items-center justify-center gap-1">
                        <span class="material-symbols-outlined text-[14px]">campaign</span> 지금 바로 홍보하세요!
                    </p>
                    <div class="flex items-center gap-2 mb-2">
                        <button class="modal-lang-btn px-3 py-1.5 rounded-lg text-xs font-bold transition-all bg-purple-600 text-white" data-lang="ko">🇰🇷 한국어</button>
                        <button class="modal-lang-btn px-3 py-1.5 rounded-lg text-xs font-bold transition-all bg-white/10 text-zinc-400 hover:bg-white/20" data-lang="en">🇺🇸 English</button>
                    </div>
                    <div class="flex gap-2">
                            <input type="text" readonly value="${shareUrl}" class="flex-1 bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-xs text-white font-mono truncate" id="modal-share-url-input">
                            <button id="modal-copy-share-btn" class="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold rounded-lg transition-all active:scale-[0.98] flex items-center gap-1 whitespace-nowrap">
                                <span class="material-symbols-outlined text-[14px]">content_copy</span> 복사
                            </button>
                        </div>
                    </div>
                    <button id="modal-confirm-btn" class="w-full py-4 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold text-lg shadow-lg shadow-indigo-500/40 transition-all active:scale-[0.98]">
                        확인
                    </button>
                </div>
            `;

            this.box.innerHTML = contentHTML;

            // Lang toggle
            const langBtns = this.box.querySelectorAll('.modal-lang-btn');
            const urlInput = this.box.querySelector('#modal-share-url-input');
            const baseUrl = shareUrl.split('?')[0];
            langBtns.forEach(btn => {
                btn.onclick = () => {
                    langBtns.forEach(b => { b.className = 'modal-lang-btn px-3 py-1.5 rounded-lg text-xs font-bold transition-all bg-white/10 text-zinc-400 hover:bg-white/20'; });
                    btn.className = 'modal-lang-btn px-3 py-1.5 rounded-lg text-xs font-bold transition-all bg-purple-600 text-white';
                    const lang = btn.dataset.lang;
                    const newUrl = lang === 'en' ? baseUrl + '?lang=en' : baseUrl;
                    if (urlInput) urlInput.value = newUrl;
                };
            });

            // Copy button
            const copyBtn = this.box.querySelector('#modal-copy-share-btn');
            if (copyBtn) {
                copyBtn.onclick = async () => {
                    const currentUrl = urlInput?.value || shareUrl;
                    try {
                        await navigator.clipboard.writeText(currentUrl);
                        copyBtn.innerHTML = '<span class="material-symbols-outlined text-[14px]">check</span> 복사됨!';
                        copyBtn.classList.replace('bg-purple-600', 'bg-green-600');
                        setTimeout(() => {
                            copyBtn.innerHTML = '<span class="material-symbols-outlined text-[14px]">content_copy</span> 복사';
                            copyBtn.classList.replace('bg-green-600', 'bg-purple-600');
                        }, 2000);
                    } catch (e) {
                        console.warn('[Modal] Clipboard copy failed:', e);
                    }
                };
            }

            const confirmBtn = this.box.querySelector('#modal-confirm-btn');
            if (confirmBtn) {
                confirmBtn.onclick = () => { this.close(); resolve(true); };
            }

            this.container.classList.remove('pointer-events-none');
            this.box.classList.remove('pointer-events-none');
            this.box.classList.add('pointer-events-auto');

            requestAnimationFrame(() => {
                this.backdrop.classList.remove('opacity-0');
                this.box.classList.remove('scale-95', 'opacity-0');
                this.box.classList.add('scale-100', 'opacity-100');
            });
        });
    }

    // [VIRAL] Show Share Prompt (from work card)
    showSharePrompt(shareUrl, seriesTitle) {
        return new Promise((resolve) => {
            if (this.container) this.container.style.display = 'flex';

            this.box.className = 'bg-white dark:bg-zinc-800 rounded-2xl shadow-2xl w-[90%] max-w-sm p-6 transform scale-95 opacity-0 transition-all duration-300 relative pointer-events-none border border-gray-100 dark:border-zinc-700';
            this.box.style.background = '';
            this.box.style.border = '';

            this.titleEl.className = 'text-lg font-bold text-gray-900 dark:text-white mb-2';
            this.messageEl.className = 'text-gray-600 dark:text-gray-300 text-sm mb-4 leading-relaxed';

            this.titleEl.innerHTML = '<span class="flex items-center gap-2"><span class="material-symbols-outlined text-purple-500">share</span> 작품 홍보</span>';
            this.messageEl.innerHTML = `<span class="font-bold text-white">${seriesTitle}</span>`;
            this.actionsEl.innerHTML = '';
            this.inputContainer.classList.add('hidden');

            // Share URL section
            let urlSection = this.box.querySelector('#modal-share-section');
            if (!urlSection) {
                urlSection = document.createElement('div');
                urlSection.id = 'modal-share-section';
                this.messageEl.after(urlSection);
            }
            urlSection.className = 'mb-4';
            urlSection.innerHTML = `
            <div class="flex items-center gap-2 mb-2">
                <button class="modal-lang-btn2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all bg-purple-600 text-white" data-lang="ko">🇰🇷 한국어</button>
                <button class="modal-lang-btn2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all bg-gray-100 dark:bg-zinc-700 text-gray-500 dark:text-zinc-400 hover:bg-gray-200 dark:hover:bg-zinc-600" data-lang="en">🇺🇸 English</button>
            </div>
            <div class="flex gap-2">
                    <input type="text" readonly value="${shareUrl}" class="flex-1 bg-gray-50 dark:bg-zinc-900 border border-gray-200 dark:border-zinc-600 rounded-lg px-3 py-2.5 text-xs text-gray-900 dark:text-white font-mono truncate" id="modal-share-url-input2">
                    <button id="modal-copy-share-btn2" class="px-4 py-2.5 bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold rounded-lg transition-all active:scale-[0.98] flex items-center gap-1 whitespace-nowrap">
                        <span class="material-symbols-outlined text-[14px]">content_copy</span> 복사
                    </button>
                </div>
                <p class="text-xs text-zinc-500 mt-2 flex items-center gap-1">
                    <span class="material-symbols-outlined text-[12px]">info</span>
                    카카오톡, 페이스북 등에 붙여넣으면 자동 미리보기가 표시됩니다
                </p>
            `;

            // Lang toggle
            const langBtns2 = urlSection.querySelectorAll('.modal-lang-btn2');
            const urlInput2 = urlSection.querySelector('#modal-share-url-input2');
            const baseUrl2 = shareUrl.split('?')[0];
            langBtns2.forEach(btn => {
                btn.onclick = () => {
                    langBtns2.forEach(b => { b.className = 'modal-lang-btn2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all bg-gray-100 dark:bg-zinc-700 text-gray-500 dark:text-zinc-400 hover:bg-gray-200 dark:hover:bg-zinc-600'; });
                    btn.className = 'modal-lang-btn2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all bg-purple-600 text-white';
                    const lang = btn.dataset.lang;
                    const newUrl = lang === 'en' ? baseUrl2 + '?lang=en' : baseUrl2;
                    if (urlInput2) urlInput2.value = newUrl;
                };
            });

            // Copy button
            const copyBtn2 = urlSection.querySelector('#modal-copy-share-btn2');
            if (copyBtn2) {
                copyBtn2.onclick = async () => {
                    const currentUrl = urlInput2?.value || shareUrl;
                    try {
                        await navigator.clipboard.writeText(currentUrl);
                        copyBtn2.innerHTML = '<span class="material-symbols-outlined text-[14px]">check</span> 복사됨!';
                        copyBtn2.classList.replace('bg-purple-600', 'bg-green-600');
                        setTimeout(() => {
                            copyBtn2.innerHTML = '<span class="material-symbols-outlined text-[14px]">content_copy</span> 복사';
                            copyBtn2.classList.replace('bg-green-600', 'bg-purple-600');
                        }, 2000);
                    } catch (e) {
                        console.warn('[Modal] Clipboard copy failed:', e);
                    }
                };
            }

            // Close button
            const closeBtn = document.createElement('button');
            closeBtn.className = 'w-full px-4 py-2 rounded-xl bg-gray-100 dark:bg-zinc-700 text-gray-500 dark:text-gray-400 font-bold text-sm hover:bg-gray-200 dark:hover:bg-zinc-600 transition-colors pointer-events-auto';
            closeBtn.textContent = '닫기';
            closeBtn.onclick = () => {
                urlSection.classList.add('hidden');
                this.close();
                resolve(true);
            };
            this.actionsEl.appendChild(closeBtn);

            this.container.classList.remove('pointer-events-none');
            this.box.classList.remove('pointer-events-none');
            this.box.classList.add('pointer-events-auto');

            requestAnimationFrame(() => {
                this.backdrop.classList.remove('opacity-0');
                this.box.classList.remove('scale-95', 'opacity-0');
                this.box.classList.add('scale-100', 'opacity-100');
            });
        });
    }

    toast(message, duration = 3000) {
        const toast = document.createElement('div');
        // [HIERARCHY] Z-Index 20050 (Highest)
        toast.className = 'fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-gray-900/90 text-white px-6 py-3 rounded-full shadow-2xl z-[20050] flex items-center gap-2 backdrop-blur-md border border-white/10 transition-all duration-300 opacity-0 translate-y-4';
        toast.innerHTML = `<span class="material-symbols-outlined text-green-400">check_circle</span> <span class="font-medium text-sm">${message}</span>`;
        document.body.appendChild(toast);

        // Animate In
        requestAnimationFrame(() => {
            toast.classList.remove('opacity-0', 'translate-y-4');
        });

        // Remove after duration
        setTimeout(() => {
            toast.classList.add('opacity-0', 'translate-y-4');
            setTimeout(() => toast.remove(), 300);
        }, duration);
    }
}

// Singleton instance
export const modal = new Modal();
